import { createFileRoute } from "@tanstack/react-router"
import { useState } from "react"
import {
	IconCash,
	IconPlus,
	IconClock,
	IconCheck,
	IconX,
} from "@tabler/icons-react"
import { AppShell } from "@/components/layout/app-shell"
import { ProtectedRoute } from "@/components/layout/protected-route"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card"
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table"
import { useMerchantWithdrawals, useCreateWithdrawal } from "@/hooks/use-merchant-withdrawals"
import { useQueryClient } from "@tanstack/react-query"
import { QUERY_KEYS } from "@/lib/constants"

export const Route = createFileRoute("/withdrawals")({
	component: WithdrawalsPage,
})

function WithdrawalsPage() {
	return (
		<ProtectedRoute>
			<AppShell>
				<WithdrawalsContent />
			</AppShell>
		</ProtectedRoute>
	)
}

const statusConfig: Record<
	string,
	{ label: string; icon: typeof IconCheck; variant: "default" | "secondary" | "destructive" | "outline" }
> = {
	pending: { label: "Pending", icon: IconClock, variant: "secondary" },
	approved: { label: "Approved", icon: IconCheck, variant: "default" },
	rejected: { label: "Rejected", icon: IconX, variant: "destructive" },
	processing: { label: "Processing", icon: IconClock, variant: "outline" },
	sent: { label: "Sent", icon: IconCheck, variant: "outline" },
	completed: { label: "Completed", icon: IconCheck, variant: "default" },
}

function WithdrawalsContent() {
	const [isOpen, setIsOpen] = useState(false)
	const queryClient = useQueryClient()
	const { data: withdrawalsData, isLoading } = useMerchantWithdrawals()
	const createWithdrawal = useCreateWithdrawal()

	const withdrawals = withdrawalsData?.withdrawals || []
	const total = withdrawalsData?.meta?.total || 0

	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault()
		const formData = new FormData(e.currentTarget)

		try {
			await createWithdrawal.mutateAsync({
				amount: Number(formData.get("amount")),
				method: 'bank_transfer',
				bankDetails: {
					accountName: formData.get("account_holder") as string || '',
					accountNumber: formData.get("bank_account") as string,
					bankName: formData.get("bank_name") as string,
					branchName: formData.get("branch_name") as string || undefined,
				},
				notes: formData.get("notes") as string || undefined,
			})
			setIsOpen(false)
			queryClient.invalidateQueries({ queryKey: QUERY_KEYS.MERCHANT_WITHDRAWALS })
			;(e.target as HTMLFormElement).reset()
		} catch (err) {
			console.error("Failed to create withdrawal:", err)
		}
	}

	return (
		<div className="space-y-6">
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-3xl font-bold tracking-tight">Withdrawals</h1>
					<p className="text-muted-foreground">
						Manage your withdrawal requests and history
					</p>
				</div>
				<Dialog open={isOpen} onOpenChange={setIsOpen}>
					<DialogTrigger asChild>
						<Button>
							<IconPlus className="h-4 w-4 mr-2" />
							New Withdrawal
						</Button>
					</DialogTrigger>
					<DialogContent>
						<DialogHeader>
							<DialogTitle>Request Withdrawal</DialogTitle>
							<DialogDescription>
								Enter the details for your withdrawal request
							</DialogDescription>
						</DialogHeader>
						<form onSubmit={handleSubmit}>
							<div className="space-y-4 py-4">
								<div className="space-y-2">
									<Label htmlFor="amount">Amount (৳)</Label>
									<Input
										id="amount"
										name="amount"
										type="number"
										placeholder="1000"
										min="100"
										required
									/>
								</div>
								<div className="space-y-2">
									<Label htmlFor="bank_name">Bank Name</Label>
									<Input
										id="bank_name"
										name="bank_name"
										placeholder="Dutch-Bangla Bank"
										required
									/>
								</div>
								<div className="space-y-2">
									<Label htmlFor="bank_account">Account Number</Label>
									<Input
										id="bank_account"
										name="bank_account"
										placeholder="1234567890"
										required
									/>
								</div>
								<div className="space-y-2">
									<Label htmlFor="account_holder">Account Holder Name</Label>
									<Input
										id="account_holder"
										name="account_holder"
										placeholder="Your Business Name"
										required
									/>
								</div>
								<div className="space-y-2">
									<Label htmlFor="branch_name">Branch Name (Optional)</Label>
									<Input
										id="branch_name"
										name="branch_name"
										placeholder="Dhanmondi Branch"
									/>
								</div>
								<div className="space-y-2">
									<Label htmlFor="notes">Notes (Optional)</Label>
									<Input
										id="notes"
										name="notes"
										placeholder="Additional notes..."
									/>
								</div>
							</div>
							<DialogFooter>
								<Button
									type="button"
									variant="outline"
									onClick={() => setIsOpen(false)}
								>
									Cancel
								</Button>
								<Button type="submit" disabled={createWithdrawal.isPending}>
									{createWithdrawal.isPending ? "Submitting..." : "Submit Request"}
								</Button>
							</DialogFooter>
						</form>
					</DialogContent>
				</Dialog>
			</div>

			{/* Withdrawals Table */}
			<Card>
				<CardHeader>
					<CardTitle>Withdrawal History</CardTitle>
					<CardDescription>
						{total} total withdrawal requests
					</CardDescription>
				</CardHeader>
				<CardContent>
					{isLoading ? (
						<div className="flex items-center justify-center h-64">
							<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
						</div>
					) : withdrawals.length === 0 ? (
						<div className="flex items-center justify-center h-64">
							<div className="text-center">
								<IconCash className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
								<p className="text-muted-foreground">No withdrawal requests yet</p>
								<Button
									variant="outline"
									className="mt-4"
									onClick={() => setIsOpen(true)}
								>
									Create your first withdrawal
								</Button>
							</div>
						</div>
					) : (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>ID</TableHead>
									<TableHead>Amount</TableHead>
									<TableHead>Method</TableHead>
									<TableHead>Account</TableHead>
									<TableHead>Status</TableHead>
									<TableHead>Notes</TableHead>
									<TableHead>Created</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{withdrawals.map((withdrawal) => {
									const config = statusConfig[withdrawal.status] || statusConfig.pending
									const StatusIcon = config.icon
									// Get display info based on method
									const methodDisplay = withdrawal.method === 'bank_transfer' ? 'Bank' : withdrawal.method.toUpperCase()
									const accountDisplay = withdrawal.method === 'bank_transfer'
										? `${withdrawal.bankDetails?.bankName || ''} - ${withdrawal.bankDetails?.accountNumber || ''}`
										: `${withdrawal.mfsDetails?.accountNumber || ''}`
									return (
										<TableRow key={withdrawal.id}>
											<TableCell className="font-medium">
												{withdrawal.id.slice(0, 8)}...
											</TableCell>
											<TableCell className="font-medium">
												৳{parseFloat(withdrawal.amount).toLocaleString()}
											</TableCell>
											<TableCell>{methodDisplay}</TableCell>
											<TableCell className="max-w-[200px] truncate">{accountDisplay}</TableCell>
											<TableCell>
												<Badge variant={config.variant}>
													<StatusIcon className="h-3 w-3 mr-1" />
													{config.label}
												</Badge>
											</TableCell>
											<TableCell className="max-w-[150px] truncate">
												{withdrawal.notes || "-"}
											</TableCell>
											<TableCell>
												{new Date(withdrawal.createdAt).toLocaleDateString()}
											</TableCell>
										</TableRow>
									)
								})}
							</TableBody>
						</Table>
					)}
				</CardContent>
			</Card>
		</div>
	)
}
